import { toast } from "sonner";
import { Video, CheckCircle, AlertTriangle, ChevronRight, Lock, Clock, Target, Copy, MessageCircle, Mail as MailIcon } from "lucide-react";

// --- Utilities -----------------------------------------------------------
const scoreLabel = (score, capped) => {
  if (capped) return "Capped";
  if (score >= 75) return "Strong";
  if (score >= 50) return "Good";
  if (score >= 30) return "Needs Work";
  return "Incomplete";
};

const sectionStatus = (c) => {
  if (!c || c.max === 0) return { label: "—", color: "text-[#555]" };
  const pct = c.score / c.max;
  if (pct >= 0.9) return { label: "Complete", color: "text-[#A3813E]" };
  if (pct >= 0.5) return { label: "Needs detail", color: "text-[#888]" };
  if (pct > 0)    return { label: "Needs detail", color: "text-[#777]" };
  return { label: "Missing", color: "text-[#666]" };
};

// --- Compact (sidebar / dashboard header) --------------------------------
export function ProfileStrengthCompact({ strength }) {
  if (!strength || strength.score === undefined) return null;
  const { score, caps } = strength;
  const capped = caps && caps.length > 0;
  return (
    <div className="flex items-baseline gap-2" data-testid="strength-compact">
      <span className="text-[10px] text-[#888] uppercase tracking-[0.15em]">Profile Strength</span>
      <span className="text-[11px] text-[#E5E5E5] font-semibold tabular-nums">{score}%</span>
      <span className="text-[10px] text-[#8a7a55]">· {scoreLabel(score, capped)}</span>
    </div>
  );
}

// --- Goal indicator -------------------------------------------------------
export function GoalIndicator({ strength }) {
  if (!strength) return null;
  const { score, actions } = strength;
  const nextAction = actions?.[0];
  return (
    <div className="px-1" data-testid="goal-indicator">
      <div className="flex items-center gap-2 mb-1.5">
        <Target size={11} className="text-[#A3813E]" />
        <span className="text-[10px] text-[#A3813E] uppercase tracking-[0.15em]">
          Goal · Publish-ready {score >= 80 ? "(Achieved)" : "(80+)"}
        </span>
      </div>
      {nextAction && score < 80 && (
        <p className="text-[11px] text-[#999] leading-relaxed pl-[22px]">
          Next: {nextAction.text} <span className="text-[#8a7a55]">({nextAction.impact})</span>
        </p>
      )}
    </div>
  );
}

// --- Video upsell ---------------------------------------------------------
export function VideoUpsell({ compact = false }) {
  return (
    <div className={`border-t border-b border-[#1A1A1A] ${compact ? "py-3" : "py-4"}`} data-testid="video-upsell">
      <div className="flex items-start gap-3">
        <Video size={14} className="text-[#A3813E] mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-[10px] font-semibold text-[#A3813E] uppercase tracking-[0.15em] mb-1">Professional video editing</p>
          <p className="text-[11px] text-[#888] leading-relaxed mb-2.5">Profiles with structured video are easier for scouts to assess.</p>
          <div className="flex gap-2">
            <a href="mailto:clips@elitescoutingnetwork.com?subject=Video%20Editing%20-%20Basic%20Package" className="text-[10px] text-[#A3813E] border-b border-[#A3813E]/40 hover:border-[#A3813E] transition-colors" data-testid="upsell-basic">
              Full Access $19
            </a>
            <span className="text-[#333]">·</span>
            <a href="mailto:clips@elitescoutingnetwork.com?subject=Video%20Editing%20-%20Pro%20Package" className="text-[10px] text-[#E5E5E5] border-b border-[#D4AF37]/40 hover:border-[#D4AF37] transition-colors" data-testid="upsell-pro">
              One-off · no subscription
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Full (editor sidebar / dashboard detail) ----------------------------
export function ProfileStrengthFull({ strength, showActions = true, onActionClick }) {
  if (!strength || strength.score === undefined) return null;
  const { score, caps, components, actions, publish_ready, checklist } = strength;
  const capped = caps && caps.length > 0;

  return (
    <div className="space-y-7" data-testid="strength-full">
      {/* Header — clean inline */}
      <div>
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[10px] font-semibold text-[#888] uppercase tracking-[0.18em]">Profile Strength</span>
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-semibold text-[#F5F5F5] tabular-nums leading-none">{score}%</span>
            <span className="text-[11px] text-[#8a7a55]">· {scoreLabel(score, capped)}</span>
          </div>
        </div>
        {/* Hairline progress — low-opacity, one bar only */}
        <div className="h-[1px] w-full bg-[#1A1A1A] rounded-full mt-3 overflow-hidden">
          <div
            className="h-full transition-all duration-700 ease-out"
            style={{ width: `${Math.max(2, score)}%`, background: capped ? "#6b5820" : "#A3813E", opacity: 0.6 }}
          />
        </div>
        {capped && caps.length > 0 && (
          <p className="text-[10px] text-[#7a6a4a] mt-2 leading-relaxed" data-testid="score-caps">
            <Lock size={9} className="inline -mt-[1px] mr-1" />{caps[0]}
          </p>
        )}
      </div>

      {/* Section status list — text-first, no bars */}
      {components && (
        <div>
          <p className="text-[10px] font-semibold text-[#888] uppercase tracking-[0.18em] mb-3">Profile Content</p>
          <ul className="space-y-2.5">
            {Object.values(components).map((c, i) => {
              const st = sectionStatus(c);
              return (
                <li key={i} className="flex items-center justify-between text-[12px] leading-none" data-testid={`section-status-${c.label}`}>
                  <span className="text-[#ccc]">{c.label}</span>
                  <span className={`text-[11px] ${st.color}`}>{st.label}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Video CTA — only if missing */}
      {components?.video?.score === 0 && <VideoUpsell compact />}

      {/* Actions — muted, minimal */}
      {showActions && actions && actions.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-[#888] uppercase tracking-[0.18em] mb-3">
            {publish_ready ? "Improve your score" : "Unlock visibility"}
          </p>
          <div className="space-y-1">
            {actions.slice(0, 3).map((a, i) => (
              <button
                key={i}
                onClick={() => onActionClick?.(a)}
                className="w-full flex items-center gap-2 px-0 py-2 text-left group border-b border-[#1A1A1A] last:border-b-0 hover:border-[#A3813E]/30 transition-colors"
                data-testid={`action-${a.field}`}
              >
                <span className="text-[11px] text-[#ccc] flex-1 leading-snug">{a.text}</span>
                <span className="text-[9px] text-[#8a7a55] flex-shrink-0">{a.impact}</span>
                {a.time && <span className="text-[9px] text-[#555] flex items-center gap-0.5 flex-shrink-0"><Clock size={9} />{a.time}</span>}
                <ChevronRight size={11} className="text-[#444] group-hover:text-[#A3813E] transition-colors flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Publish Readiness — clean text list */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          {publish_ready ? (
            <>
              <CheckCircle size={11} className="text-[#6b8f6b]" />
              <span className="text-[10px] font-semibold text-[#6b8f6b] uppercase tracking-[0.18em]">Ready to publish</span>
            </>
          ) : (
            <>
              <AlertTriangle size={11} className="text-[#8f7d4a]" />
              <span className="text-[10px] font-semibold text-[#8f7d4a] uppercase tracking-[0.18em]">Not ready</span>
            </>
          )}
        </div>
        <ul className="space-y-1.5">
          {(checklist || []).map((c, i) => (
            <li key={i} className="flex items-center gap-2.5">
              {c.done ? (
                <CheckCircle size={10} className="text-[#A3813E] flex-shrink-0" />
              ) : (
                <span className="w-[10px] h-[10px] rounded-full border border-[#2a2a2a] flex-shrink-0" />
              )}
              <span className={`text-[11px] ${c.done ? "text-[#888]" : c.required ? "text-[#ccc]" : "text-[#666]"}`}>
                {c.label}{c.required && !c.done ? " *" : ""}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// --- Post-publish share ---------------------------------------------------
export function PostPublishActions({ profileUrl, identitySummary }) {
  if (!profileUrl) return null;
  const playerName = identitySummary?.name || "";
  const position = identitySummary?.position || "";
  const shareMsg = `Hi, I'm ${playerName}${position ? `, a ${position}` : ""}. Here is my full professional scouting profile: ${profileUrl}`;

  return (
    <div className="space-y-3" data-testid="post-publish-actions">
      <div className="text-[10px] font-semibold text-[#888] uppercase tracking-[0.18em]">Share your profile</div>
      <p className="text-[11px] text-[#888] leading-relaxed">Your profile is live. Send it directly to scouts, agents, and clubs.</p>
      <div className="space-y-1.5">
        <button onClick={() => { navigator.clipboard.writeText(profileUrl); toast.success("Link copied!"); }} className="w-full flex items-center gap-2 bg-[#D4AF37] text-black font-bold uppercase tracking-wider text-[10px] px-3 py-2 rounded-sm hover:bg-[#C5A059] transition-colors" data-testid="share-copy-link">
          <Copy size={12} /> Copy Profile Link
        </button>
        <a href={`https://wa.me/?text=${encodeURIComponent(shareMsg)}`} target="_blank" rel="noopener noreferrer" className="w-full flex items-center gap-2 bg-[#1A1A1A] border border-[#262626] text-[#E5E5E5] font-bold uppercase tracking-wider text-[10px] px-3 py-2 rounded-sm hover:border-[#A3813E]/50 transition-colors" data-testid="share-whatsapp">
          <MessageCircle size={12} /> Share on WhatsApp
        </a>
        <a href={`mailto:?subject=${encodeURIComponent(`Player Profile - ${playerName}`)}&body=${encodeURIComponent(shareMsg)}`} className="w-full flex items-center gap-2 bg-[#1A1A1A] border border-[#262626] text-[#E5E5E5] font-bold uppercase tracking-wider text-[10px] px-3 py-2 rounded-sm hover:border-[#A3813E]/50 transition-colors" data-testid="share-email">
          <MailIcon size={12} /> Send to Clubs via Email
        </a>
        {identitySummary?.headline && (
          <button onClick={() => { navigator.clipboard.writeText(`${playerName} — ${identitySummary.headline}`); toast.success("Summary copied!"); }} className="w-full flex items-center gap-2 bg-[#1A1A1A] border border-[#262626] text-[#E5E5E5] font-bold uppercase tracking-wider text-[10px] px-3 py-2 rounded-sm hover:border-[#A3813E]/50 transition-colors" data-testid="share-copy-summary">
            <Copy size={12} /> Copy Identity Summary
          </button>
        )}
      </div>
    </div>
  );
}
